/**
 * Sync Service
 * 
 * Coordinates cross-device synchronization by managing device registration,
 * offline queues, real-time updates, and conflict resolution.
 */

import { getDeviceManager } from './device-manager';
import { getWebSocketClient, SyncEvent } from './websocket-client';
import { apiRequest } from '@/lib/queryClient';

export interface SyncMessage {
  id?: string;
  conversationId: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  metadata?: any;
  clientMessageId?: string;
  timestamp?: string;
}

export interface SyncConversation {
  id: string;
  title: string;
  description?: string;
  tags?: string[];
  status: string;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  last_activity_at?: string;
  unreadCount?: number;
  messages?: SyncMessage[];
}

export interface SyncUpdate {
  type: string;
  conversationId: string;
  messageId?: string;
  globalSequence: number;
  data: any;
  timestamp: string;
}

class SyncService {
  private deviceManager = getDeviceManager();
  private wsClient = getWebSocketClient();
  private isInitialized = false;
  private syncInProgress = false;
  
  // Event listeners
  private messageListeners: Array<(message: SyncMessage, conversationId: string) => void> = [];
  private conversationListeners: Array<(conversation: SyncConversation) => void> = [];
  private readStateListeners: Array<(conversationId: string, deviceId: string, data: any) => void> = [];

  /**
   * Initialize sync service
   */
  async initialize(authToken: string): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    console.log('🔄 Initializing sync service...');

    try {
      // Register device with backend
      const registered = await this.deviceManager.registerDevice(apiRequest);
      if (!registered) {
        console.warn('⚠️ Device registration failed, continuing without sync');
      }

      // Connect WebSocket
      await this.wsClient.connect(authToken);

      // Setup event listeners
      this.setupEventListeners();

      // Perform initial sync
      await this.performIncrementalSync();

      // Process offline queue if any
      await this.processOfflineQueue();

      this.isInitialized = true;
      console.log('✅ Sync service initialized');

    } catch (error) {
      console.error('❌ Failed to initialize sync service:', error);
      throw error;
    }
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupEventListeners(): void {
    // Listen for all sync events
    this.wsClient.addEventListener('*', (event: SyncEvent) => {
      this.handleSyncEvent(event);
    });

    // Listen for connection status changes
    this.wsClient.onStatusChange((status) => {
      if (status.connected && !this.syncInProgress) {
        // Perform sync when reconnected
        this.performIncrementalSync();
      }
    });

    // Listen for device status changes (online/offline)
    this.deviceManager.onStatusChange((status) => {
      if (status.isOnline && !this.syncInProgress) {
        // Process offline queue when coming back online
        this.processOfflineQueue();
      }
    });
  }

  /**
   * Handle incoming sync events
   */
  private handleSyncEvent(event: SyncEvent): void {
    console.log('⚡ Processing sync event:', event.type, event.conversationId);

    switch (event.type) {
      case 'message.created':
        this.handleMessageCreated(event);
        break;
      
      case 'message.updated':
        this.handleMessageUpdated(event);
        break;
      
      case 'conversation.updated':
        this.handleConversationUpdated(event);
        break;
      
      case 'conversation.deleted':
        this.handleConversationDeleted(event);
        break;
      
      case 'read_state.updated':
        this.handleReadStateUpdated(event);
        break;
    }

    // Update last sync sequence
    this.deviceManager.updateLastSyncSequence(event.sequenceNumber);
  }

  /**
   * Handle message created event
   */
  private handleMessageCreated(event: SyncEvent): void {
    const message: SyncMessage = {
      id: event.messageId,
      conversationId: event.conversationId,
      content: event.data.content || '',
      role: event.data.role || 'user',
      metadata: event.data.metadata || {},
      clientMessageId: event.data.clientMessageId,
      timestamp: event.timestamp.toISOString()
    };

    // Notify listeners
    this.messageListeners.forEach(listener => {
      try {
        listener(message, event.conversationId);
      } catch (error) {
        console.error('❌ Error in message listener:', error);
      }
    });
  }

  /**
   * Handle message updated event
   */
  private handleMessageUpdated(event: SyncEvent): void {
    // Similar to message created, but for updates
    this.handleMessageCreated(event);
  }

  /**
   * Handle conversation updated event
   */
  private handleConversationUpdated(event: SyncEvent): void {
    // Fetch updated conversation data
    this.fetchConversation(event.conversationId);
  }

  /**
   * Handle conversation deleted event
   */
  private handleConversationDeleted(event: SyncEvent): void {
    // Notify listeners about deletion
    const conversation: SyncConversation = {
      id: event.conversationId,
      title: '',
      status: 'deleted',
      created_at: '',
      updated_at: event.timestamp.toISOString()
    };

    this.conversationListeners.forEach(listener => {
      try {
        listener(conversation);
      } catch (error) {
        console.error('❌ Error in conversation listener:', error);
      }
    });
  }

  /**
   * Handle read state updated event
   */
  private handleReadStateUpdated(event: SyncEvent): void {
    this.readStateListeners.forEach(listener => {
      try {
        listener(event.conversationId, event.data.deviceId, event.data);
      } catch (error) {
        console.error('❌ Error in read state listener:', error);
      }
    });
  }

  /**
   * Send message with sync support
   */
  async sendMessage(conversationId: string, content: string, role: 'user' | 'assistant' = 'user', metadata?: any): Promise<SyncMessage | null> {
    const deviceManager = this.deviceManager;
    const message: SyncMessage = {
      conversationId,
      content,
      role,
      metadata: metadata || {},
      clientMessageId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
      timestamp: new Date().toISOString()
    };

    // If offline, store message locally
    if (!deviceManager.getSyncStatus().isOnline) {
      deviceManager.storeOfflineMessage(message);
      console.log('📱 Message stored offline:', message.clientMessageId);
      return message;
    }

    try {
      // Send via sync API
      const response = await apiRequest('POST', `/api/sync/conversations/${conversationId}/messages`, {
        content: message.content,
        role: message.role,
        metadata: message.metadata,
        clientMessageId: message.clientMessageId
      }, {
        headers: deviceManager.getSyncHeaders()
      }) as any;

      if (response.id) {
        message.id = response.id;
        console.log('✅ Message sent successfully:', message.clientMessageId);
        return message;
      }

      throw new Error('Invalid response from server');

    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Store offline for retry
      deviceManager.storeOfflineMessage(message);
      return message;
    }
  }

  /**
   * Get conversations with sync metadata
   */
  async getConversations(): Promise<SyncConversation[]> {
    try {
      const deviceManager = this.deviceManager;
      const response = await apiRequest('GET', '/api/sync/conversations', {}, {
        headers: deviceManager.getSyncHeaders()
      }) as any;

      return response.conversations || [];
    } catch (error) {
      console.error('❌ Failed to get conversations:', error);
      return [];
    }
  }

  /**
   * Get conversation with sync metadata
   */
  async getConversation(conversationId: string): Promise<SyncConversation | null> {
    try {
      const deviceManager = this.deviceManager;
      const response = await apiRequest('GET', `/api/sync/conversations/${conversationId}`, {}, {
        headers: deviceManager.getSyncHeaders()
      }) as SyncConversation | null;

      // Subscribe to real-time updates for this conversation
      this.wsClient.subscribeToConversation(conversationId);

      return response;
    } catch (error) {
      console.error('❌ Failed to get conversation:', error);
      return null;
    }
  }

  /**
   * Fetch conversation (for updates)
   */
  private async fetchConversation(conversationId: string): Promise<void> {
    try {
      const conversation = await this.getConversation(conversationId);
      if (conversation) {
        this.conversationListeners.forEach(listener => {
          try {
            listener(conversation);
          } catch (error) {
            console.error('❌ Error in conversation listener:', error);
          }
        });
      }
    } catch (error) {
      console.error('❌ Failed to fetch conversation:', error);
    }
  }

  /**
   * Update read state
   */
  async updateReadState(conversationId: string, lastReadMessageId: string): Promise<void> {
    try {
      const deviceManager = this.deviceManager;
      await apiRequest('PUT', `/api/sync/conversations/${conversationId}/read-state`, {
        lastReadMessageId
      }, {
        headers: deviceManager.getSyncHeaders()
      });

      console.log('✅ Read state updated:', conversationId, lastReadMessageId);
    } catch (error) {
      console.error('❌ Failed to update read state:', error);
    }
  }

  /**
   * Perform incremental sync
   */
  async performIncrementalSync(): Promise<void> {
    if (this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;
    console.log('🔄 Performing incremental sync...');

    try {
      const deviceManager = this.deviceManager;
      const lastSequence = deviceManager.getLastSyncSequence();

      const response = await apiRequest('GET', `/api/sync/updates?sinceSequence=${lastSequence}`, undefined, {
        headers: deviceManager.getSyncHeaders()
      }) as any;

      const updates: SyncUpdate[] = response.updates || [];
      console.log(`📥 Received ${updates.length} sync updates`);

      // Process updates in sequence order
      for (const update of updates) {
        const event: SyncEvent = {
          type: update.type as any,
          conversationId: update.conversationId,
          messageId: update.messageId,
          data: update.data,
          timestamp: new Date(update.timestamp),
          sequenceNumber: update.globalSequence,
          userId: '', // Will be filled by backend
          deviceId: '' // Will be filled by backend
        };

        this.handleSyncEvent(event);
      }

      // Update last sync sequence
      if (updates.length > 0) {
        const latestSequence = Math.max(...updates.map(u => u.globalSequence));
        deviceManager.updateLastSyncSequence(latestSequence);
      }

      console.log('✅ Incremental sync completed');

    } catch (error) {
      console.error('❌ Incremental sync failed:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Process offline message queue
   */
  async processOfflineQueue(): Promise<void> {
    const deviceManager = this.deviceManager;
    const offlineMessages = deviceManager.getOfflineMessages();

    if (offlineMessages.length === 0) {
      return;
    }

    console.log(`📤 Processing ${offlineMessages.length} offline messages...`);

    try {
      const response = await apiRequest('POST', '/api/sync/offline-queue', {
        messages: offlineMessages
      }, {
        headers: deviceManager.getSyncHeaders()
      }) as any;

      if (response.success) {
        console.log(`✅ Processed ${response.successful}/${response.processed} offline messages`);
        deviceManager.clearOfflineMessages();
      } else {
        console.error('❌ Failed to process offline queue:', response);
      }

    } catch (error) {
      console.error('❌ Failed to process offline queue:', error);
    }
  }

  /**
   * Add message listener
   */
  onMessage(listener: (message: SyncMessage, conversationId: string) => void): () => void {
    this.messageListeners.push(listener);
    
    return () => {
      const index = this.messageListeners.indexOf(listener);
      if (index > -1) {
        this.messageListeners.splice(index, 1);
      }
    };
  }

  /**
   * Add conversation listener
   */
  onConversation(listener: (conversation: SyncConversation) => void): () => void {
    this.conversationListeners.push(listener);
    
    return () => {
      const index = this.conversationListeners.indexOf(listener);
      if (index > -1) {
        this.conversationListeners.splice(index, 1);
      }
    };
  }

  /**
   * Add read state listener
   */
  onReadState(listener: (conversationId: string, deviceId: string, data: any) => void): () => void {
    this.readStateListeners.push(listener);
    
    return () => {
      const index = this.readStateListeners.indexOf(listener);
      if (index > -1) {
        this.readStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get active devices
   */
  async getActiveDevices(): Promise<any[]> {
    try {
      const deviceManager = this.deviceManager;
      const response = await apiRequest('GET', '/api/sync/devices', {}, {
        headers: deviceManager.getSyncHeaders()
      }) as any;

      return response.devices || [];
    } catch (error) {
      console.error('❌ Failed to get active devices:', error);
      return [];
    }
  }

  /**
   * Cleanup sync service
   */
  cleanup(): void {
    this.wsClient.disconnect();
    this.messageListeners = [];
    this.conversationListeners = [];
    this.readStateListeners = [];
    this.isInitialized = false;
  }

  /**
   * Check if sync is available
   */
  isSyncAvailable(): boolean {
    return this.isInitialized && this.wsClient.isConnected();
  }

  /**
   * Get sync status
   */
  getSyncStatus() {
    return {
      initialized: this.isInitialized,
      websocket: this.wsClient.getConnectionStatus(),
      device: this.deviceManager.getSyncStatus()
    };
  }
}

// Singleton instance
let syncServiceInstance: SyncService | null = null;

export function getSyncService(): SyncService {
  if (!syncServiceInstance) {
    syncServiceInstance = new SyncService();
  }
  return syncServiceInstance;
}

export { SyncService };